import type Highcharts from 'highcharts';
import moment from 'moment';

import type { BlockThemeConfig, DChartInterface } from 'webapp-types';
import { GoalStatus } from 'webapp/enums/enums';
import { formatNumber, getIntervalTime } from 'webapp/utils/number';

import type VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import type VisualizationProgress from 'webapp/models-custom/visualization/progress';
import type VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import type Series from 'webapp/d-chart/series/series';

interface PointLike {
	value: number | null;
	valueStr: string;
}

export default abstract class ProgressOrGaugeBaseRenderer extends BaseRenderer {
	override get visualization():
		VisualizationProgress | VisualizationRadialProgress | VisualizationGauge {
		return super.visualization as
			VisualizationProgress | VisualizationRadialProgress | VisualizationGauge;
	}

	get firstSeries() {
		return this.visualization.series[0];
	}

	get primaryPoint() {
		if (this.firstSeries) {
			const { primaryPoint } = this.getPointsForSeries(this.firstSeries);
			return primaryPoint;
		}
		return null;
	}

	get percent(): number {
		if (this.firstSeries) {
			return this.getValueProgressForSeries(this.firstSeries);
		}
		return 0;
	}

	get gradientStepId() {
		return this.getGradientStepIdForPercent(this.percent, this.visualization.isInverted);
	}

	getGradientStepIdForPercent(
		percent: number,
		isInverted: boolean,
		goalStatus: GoalStatus | null = null,
	): number {
		const isTrafficLightsEnabled = this.isTrafficLightsEnabled;

		if (isTrafficLightsEnabled && goalStatus) {
			const onTrackIndex = 3,
				atRiskIndex = 2,
				offTrackIndex = 1;

			if (goalStatus === GoalStatus.ON_TRACK || goalStatus === GoalStatus.ACHIEVED) {
				return onTrackIndex;
			} else if (goalStatus === GoalStatus.OFF_TRACK || goalStatus === GoalStatus.MISSED) {
				return offTrackIndex;
			} else {
				return atRiskIndex;
			}
		} else {
			const ratio = Math.min(0.99, percent / 100);
			const steps = this.dChart.themeData.visualizationConfig[
				isTrafficLightsEnabled ? 'trafficLightsSteps' : 'steps'
			] as BlockThemeConfig.GradientStep[] | undefined;
			let gradientStepId =
				(steps || []).findIndex(
					(step) => step.range[0] <= ratio && step.range[1] > ratio,
				) || 0;

			gradientStepId++;

			if (isInverted) {
				if (gradientStepId === 1) {
					gradientStepId = 3;
				} else if (gradientStepId === 3) {
					gradientStepId = 1;
				}
			}

			return gradientStepId;
		}
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				margin: [0, 0, 0, 0],
			},
			legend: {
				enabled: false,
			},
			xAxis: {
				visible: false,
			},
			plotOptions: {
				series: {
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								((event?.point as DChartInterface.ChartHoverPoint | undefined)
									?.dataLabel?.element?.firstChild as SVGElement) ||
									event?.srcElement,
							);
						},
					},
				},
			},
		});

		return options;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;

		const defs: Record<string, DChartInterface.Def> = {},
			gradientDefs = this.gradientDefs;

		gradientDefs.forEach((def) => {
			defs[def.id] = def;
		});

		options = this.mergeOptions(options, { defs });

		return options;
	}

	get isTrafficLightsEnabled() {
		return this.visualization.blockSetup.block.showTrafficLights;
	}

	abstract get gradientDefs(): DChartInterface.Def[];

	override updateTheme(): void {
		let options = this.themeOptions;

		// merge with data options since they set colorIndex
		options = this.mergeOptions(options, this.dataOptions);

		this.dChart.updateHChart(options);

		this.updateDefs();
	}

	protected override getDefs() {
		return this.gradientDefs;
	}

	getPointsForSeries(series: Series) {
		// minPoint is the defined minPoint not the actual mathematical min (same for max)
		// use these to read direct setups (e.g. goal info from maxPoint)
		// also can use forward in this.getMinMaxValues to get actual mathematical min/max
		return {
			primaryPoint: series.points[1] ?? null,
			minPoint: series.points[0] ?? null,
			maxPoint: series.points[2] ?? null,
		};
	}

	getMinMaxValues(point1: PointLike, point2: PointLike) {
		// this will return actual mathematical min or max from the two given points
		// since user could have 20 in min and 10 in max
		// use this for logic & showing max valueStr
		const point1Value = point1.value || 0,
			point2Value = point2.value || 0;

		const minPoint = point1Value <= point2Value ? point1 : point2,
			maxPoint = point1Value > point2Value ? point1 : point2;
		return {
			min: {
				value: minPoint.value,
				valueStr: minPoint.valueStr,
			},
			max: {
				value: maxPoint.value,
				valueStr: maxPoint.valueStr,
			},
		};
	}

	getValueProgressForSeries(series: Series) {
		const { primaryPoint, minPoint, maxPoint } = this.getPointsForSeries(series);
		if (primaryPoint) {
			return this.getValueProgressForPoints(primaryPoint, minPoint!, maxPoint!);
		}
		return 0;
	}

	protected getValueProgressForPoints(
		primaryPoint: PointLike,
		minPoint: PointLike,
		maxPoint: PointLike,
	) {
		let progress = 0;
		if (primaryPoint) {
			const { min, max } = this.getMinMaxValues(minPoint, maxPoint);
			const value = primaryPoint.value || 0;
			const minValue = min.value || 0;
			const maxValue = max.value || 0;

			if (value > minValue && maxValue > minValue) {
				progress = (value - minValue) / (maxValue - minValue);
			}
		}
		return +(progress * 100).toFixed(2);
	}

	protected formatPercentBubble(): string {
		let formattedPercent: string | undefined = this.primaryPoint?.comparePoints.find(
			(point) => point.isGoal,
		)?.changeStr;

		const percent = this.percent;
		if (!formattedPercent && percent) {
			formattedPercent = `${formatNumber(percent, '#.[##]')}%`;
		}

		return formattedPercent
			? `<div class="progress-goal"><div class="highcharts-dbx-percent-bubble">${formattedPercent}</div><span> of your goal</span></div>`
			: '';
	}

	protected formatTimeLeft(periodEndTS?: number): string {
		const timeLeftTS = (periodEndTS ?? (this.visualization.periodEndTS || 0)) - moment().unix();
		let timeLeftText = '';

		if (timeLeftTS > 0) {
			timeLeftText = `${getIntervalTime(timeLeftTS, 'seconds', true)} left`;
		}

		return timeLeftText;
	}
}
